import { PageHero } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { PortalButton } from "@/components/PortalButton";
import { UserPlus, LogIn, PackageCheck, ShieldCheck } from "lucide-react";

const STEPS = [
  { icon: LogIn, title: "Sign in or create your BlueFig account", desc: "Customer profiles, login details and account security are managed directly inside BlueFig." },
  { icon: PackageCheck, title: "Browse wholesale products", desc: "Approved customers can view available products, customer-specific pricing and ordering options." },
  { icon: ShieldCheck, title: "Order through one secure account", desc: "Your orders, profile and account history stay connected to your BlueFig customer profile." },
];

export default function BecomeCustomer() {
  return (
    <>
      <PageHero
        chapter="Become a Customer"
        title="Open your wholesale account through BlueFig"
        lead="Brampton Cash & Carry uses BlueFig for customer accounts, login, profiles and online ordering. There are no customer accounts stored on this website."
      >
        <PortalButton variant="butter" testId="become-customer-bluefig-btn">Login / Sign Up on BlueFig</PortalButton>
      </PageHero>

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <Reveal className="rounded-3xl border border-forest/15 bg-white p-8 md:p-10 shadow-hard" data-testid="become-customer-info">
            <p className="overline text-forest/55">One Account for Ordering</p>
            <h2 className="font-display text-3xl md:text-4xl text-forest tracking-tight mt-3 max-w-2xl">
              Everything account-related happens in the BlueFig portal.
            </h2>
            <p className="text-forest/70 mt-5 max-w-3xl leading-relaxed">
              Select the Login / Sign Up button to open the Brampton Cash & Carry vendor page in BlueFig. From there, you can sign in, create your customer profile and access the available ordering features.
            </p>
            <div className="mt-8">
              <PortalButton variant="solid" testId="become-customer-bluefig-secondary-btn">Continue to BlueFig</PortalButton>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {STEPS.map(({ icon: Icon, title, desc }, index) => (
              <Reveal key={title} delay={index * 0.08} className="rounded-3xl border border-forest/15 bg-white p-6">
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-forest text-butter mb-5"><Icon className="h-5 w-5" /></span>
                <h3 className="font-display text-xl text-forest">{title}</h3>
                <p className="text-sm text-forest/70 mt-2 leading-relaxed">{desc}</p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="mt-10 rounded-2xl bg-sage/60 border border-forest/10 p-5 flex gap-4">
            <UserPlus className="h-5 w-5 text-forest shrink-0 mt-0.5" />
            <p className="text-sm text-forest/75 leading-relaxed">
              New customer approval, pricing access and account availability are handled by the BlueFig vendor account and wholesale team.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
